/* import __COLOCATED_TEMPLATE__ from './let-fin-introduce-itself.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import type { FinSetupBehaviorSaveParams } from 'embercom/lib/operator/fin/types';
import type IntlService from 'embercom/services/intl';
import type Profile from 'embercom/models/ai-agent/profile';
import { updateUsingDefaultFlags } from 'embercom/utils/bot-intro';

interface Args {
  accordion: $TSFixMe;
  ruleset: $TSFixMe;
  previewConfiguration: $TSFixMe;
  partialSave: (params: FinSetupBehaviorSaveParams) => void;
  isEmail: boolean;
}

export default class LetFinIntroduceItself extends Component<Args> {
  @service declare contentEditorService: $TSFixMe;
  @service declare appService: AppService;
  @service declare store: Store;
  @service declare notificationsService: $TSFixMe;
  @service declare intl: IntlService;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
  }

  get ruleset() {
    return this.args.ruleset;
  }

  get behavior(): Profile {
    return this.ruleset.rulesetLinks.firstObject.object;
  }

  get localizations() {
    return this.behavior.botIntroLocalizations;
  }

  get shouldHideAiDisclosure() {
    return this.ruleset.createdAt >= new Date('2025-03-27');
  }

  get summaryText() {
    // Safety check for localizations
    if (!this.localizations || !this.localizations.length) {
      return this.intl.t('operator.fin.setup.setup-and-go-live.fin-introduction.summary.disabled');
    }

    let hasMessages = this.localizations
      .toArray()
      .some((localization: any) => localization.parts && localization.parts.length > 0);
    return hasMessages
      ? this.intl.t('operator.fin.setup.setup-and-go-live.fin-introduction.summary.enabled', {
          localeList: this.intl.t(
            'operator.fin.setup.setup-and-go-live.handover.all-supported-languages',
          ),
        })
      : this.intl.t('operator.fin.setup.setup-and-go-live.fin-introduction.summary.disabled');
  }

  get introText() {
    if (this.args.isEmail) {
      return this.intl.t(
        'operator.fin.setup.setup-and-go-live.fin-introduction.body.description-email',
        { htmlSafe: true },
      );
    }
    return this.intl.t('operator.fin.setup.setup-and-go-live.fin-introduction.body.description', {
      htmlSafe: true,
    });
  }

  get isEmail() {
    return this.args.isEmail;
  }

  get invalidBotIntroLocales() {
    // only consider the default localization of the app to check the validity of the parts
    let defaultLocalization = this.localizations.length ? this.localizations[0] : null;
    if (!defaultLocalization) {
      return [];
    }

    return defaultLocalization.parts
      .filter((part: $TSFixMe) => !part.validations.isValid)
      .map((part: $TSFixMe) => part.locale);
  }

  get hasInvalidBotIntro() {
    return this.invalidBotIntroLocales.length > 0;
  }

  get invalidBotIntroError() {
    return this.intl.t('operator.fin.setup.setup-and-go-live.empty-message-part-error', {
      languages: this.invalidBotIntroLocales.join(', '),
    });
  }

  get shouldShowMigrationBanner() {
    // Check if there are existing localizations with content in multiple languages
    let localizationsWithContent = this.localizations.filter((localization: any) => {
      return localization.parts && localization.parts.length > 0;
    });

    // Show banner if there are 2 or more languages with content (indicating manual setup)
    return localizationsWithContent.length > 1;
  }

  @action
  async updateBotIntroLocalizations() {
    updateUsingDefaultFlags(this.localizations.toArray());

    let bot_intro_localizations = this.localizations.serialize().map((l: any) => {
      return {
        locale: l.locale,
        enabled: l.enabled,
        parts: l.parts,
        using_default: l.using_default,
      };
    });

    this.args.partialSave({ bot_intro_localizations });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::Sections::LetFinIntroduceItself': typeof LetFinIntroduceItself;
  }
}
